import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { bulkUploadVideos, downloadBulkUploadSample } from "./api";

const ALLOWED_EXTENSIONS = ["xlsx", "xls", "csv"];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // matches the API's max:10240 (KB)
const SAMPLE_FILE_NAME = "video_bulk_upload_sample.xlsx";

const useBulkUpload = ({ refetch, handleClose }) => {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [rowErrors, setRowErrors] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");

  const resetForm = () => {
    setFile(null);
    setFileError("");
    setRowErrors([]);
    setErrorMessage("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCancel = () => {
    resetForm();
    handleClose();
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setRowErrors([]);
    setErrorMessage("");
    if (!selected) {
      setFile(null);
      return;
    }
    const extension = selected.name.split(".").pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      setFile(null);
      setFileError("*Only .xlsx, .xls or .csv files are allowed");
      e.target.value = "";
      return;
    }
    if (selected.size > MAX_FILE_SIZE) {
      setFile(null);
      setFileError("*File must be smaller than 10 MB");
      e.target.value = "";
      return;
    }
    setFileError("");
    setFile(selected);
  };

  const handleUpload = async (e) => {
    e?.preventDefault?.();
    if (!file) {
      setFileError("*Select an Excel file to upload");
      return;
    }
    setIsUploading(true);
    setRowErrors([]);
    setErrorMessage("");

    const formData = new FormData();
    formData.append("file", file);
    const response = await bulkUploadVideos(formData);
    setIsUploading(false);

    if (response?.status === 200 && response?.data?.success) {
      const queued = response?.data?.data?.queued_rows;
      toast.success(
        `${response?.data?.message ?? `${queued} videos queued for processing`}. They are added as inactive; activate them from the list once processing finishes.`
      );
      resetForm();
      handleClose();
      refetch?.();
    } else if (Array.isArray(response?.data?.errors)) {
      // row-level errors from the sheet: nothing was created
      setRowErrors(response.data.errors);
      setErrorMessage(response?.message ?? "Some rows are invalid. No videos were created.");
      toast.error(response?.message ?? "Some rows are invalid");
    } else if (response?.errors?.file) {
      setFileError(`*${response.errors.file}`);
    } else {
      const message = response?.message ?? response?.msg ?? "Something went wrong";
      setErrorMessage(message);
      toast.error(message);
    }
  };

  const handleSampleDownload = async () => {
    setIsDownloading(true);
    const response = await downloadBulkUploadSample();
    setIsDownloading(false);

    if (response?.status === 200) {
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.setAttribute("download", SAMPLE_FILE_NAME);
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(downloadLink.href);
    } else {
      // error bodies arrive as a Blob because of responseType "blob"
      let message = "Unable to download the sample file";
      try {
        const body = JSON.parse(await response?.data?.text());
        message = body?.message ?? body?.msg ?? message;
      } catch (err) {}
      toast.error(message);
    }
  };

  return {
    file,
    fileError,
    fileInputRef,
    isUploading,
    isDownloading,
    rowErrors,
    errorMessage,
    handleFileChange,
    handleUpload,
    handleSampleDownload,
    handleCancel,
  };
};

export default useBulkUpload;
